import React, { useCallback, useEffect, useState } from "react";

import {
  isAuthed,
  getToken,
  clearToken,
  goToPlatformLogin,
  hasPlatformLoginUrl,
} from "../auth";
import api from "../utils/api";

// How often to check the shared cookie is still present (cheap, no network).
const COOKIE_POLL_MS = 4000;
// How often to revalidate the token with the API (network — catches a token
// that was revoked/expired server-side even if a stale cookie lingers).
const REVALIDATE_MS = 60000;

// Gates the dashboard behind the platform session (the shared Cognito token
// cookie). No session → bounce to the platform login. If no platform URL is
// configured (e.g. local dev), show a message instead of looping.
//
// The session can also end while the tab is open — the admin logs out on the
// platform, which clears the shared `.vasana.ai` cookie and (ideally) revokes
// the token server-side. An idle dashboard tab makes no requests, so it would
// otherwise never notice. We actively watch for that and end the session.
export default function ProtectedRoute({ children }) {
  const [authed, setAuthed] = useState(isAuthed());

  // Drop the session locally: forget the (now stale) cookie and flip to the
  // signed-out view, which redirects to the platform login when configured.
  const endSession = useCallback(() => {
    clearToken();
    setAuthed(false);
  }, []);

  // Ask the API whether the token is still a valid platform-admin session.
  // Reaches 200 only for admins; 401 = token gone/expired/revoked, 403 = no
  // longer a platform admin. Either way, end the session.
  const revalidate = useCallback(async () => {
    if (!getToken()) {
      endSession();
      return;
    }
    try {
      await api.get("/api/me");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) endSession();
    }
  }, [endSession]);

  useEffect(() => {
    if (!authed) {
      if (hasPlatformLoginUrl()) goToPlatformLogin();
      return undefined;
    }

    // 1) Cheap, frequent check: the platform clears the shared `.vasana.ai`
    //    cookie on logout — notice it even while the tab sits idle.
    const cookieTimer = setInterval(() => {
      if (!getToken()) endSession();
    }, COOKIE_POLL_MS);

    // 2) Periodic + on-focus server revalidation: catches a token revoked or
    //    expired server-side (e.g. Cognito global sign-out) even if the cookie
    //    is still around.
    const serverTimer = setInterval(revalidate, REVALIDATE_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") revalidate();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", revalidate);
    revalidate(); // also check immediately on mount

    return () => {
      clearInterval(cookieTimer);
      clearInterval(serverTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", revalidate);
    };
  }, [authed, endSession, revalidate]);

  if (authed) return children;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "radial-gradient(120% 90% at 50% -10%, #2a1330 0%, #150a1b 45%, #0e0610 100%)",
        color: "rgba(255,255,255,0.75)",
        fontFamily: '"DM Sans", system-ui, sans-serif',
        textAlign: "center",
      }}
    >
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
          Sign in required
        </div>
        <div style={{ fontSize: 14 }}>
          {hasPlatformLoginUrl()
            ? "Redirecting you to the Vasana platform to sign in…"
            : "Please open this dashboard from the Vasana platform after signing in."}
        </div>
      </div>
    </div>
  );
}
